import Modal from "@/components/controls/Modal";
import Tabs from "@/components/controls/Tabs";
import Text from "@/components/ui/text";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MILESTONES,
  STAGES,
  StageAverageDurationChartDataType
} from "@/components/layouts/StageAverageDuration/StageAverageDuration";
import Dropdown from "@/components/controls/Dropdown/Dropdown";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/utils/utils";
import DurationDrilldown from "./DurationDrilldown";
import UserBreadcrumbs from "@/components/controls/UserBreadcrumbs/UserBreadcrumbs";
import { getColorForMilestoneByType } from "@/components/shared/Milestones";
import { useStageDurationBreadcrumbs } from "@/components/layouts/StageAverageDuration/StageDurationBreadcrumbsContext";
import {
  AverageTimeSpentDirectReportsResponse,
  SettingsPeriod
} from "@/entities/reporting/types";
import { periodsDropdownProps } from "@/components/layouts/ConversionRates/helpers";
import {
  periodEnumToString,
  periodStringToEnum
} from "@/entities/reporting/helpers";
import FeedbackButton from "@/components/controls/FeedbackButton";
import { camelCase, find, groupBy, isEmpty, map } from "lodash";
import { DealMilestone } from "@/pages/deals/types";
import { daysToWeeks } from "date-fns";
import { useDealStages } from "@/entities/settings/deal/hooks";
import { createDataMapById } from "@/utils/datamaps";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  chartData: {
    [key: string]: StageAverageDurationChartDataType[];
  };
  directReportsData?: AverageTimeSpentDirectReportsResponse;
  mainSelectedStageId: string;
  mainSelectedTab: string;
  period: SettingsPeriod;
  periodSelect: (period: SettingsPeriod) => void;
}

const DurationDrilldownModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  chartData,
  directReportsData,
  mainSelectedStageId,
  mainSelectedTab,
  period,
  periodSelect
}) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(STAGES);
  const [selectedStageId, setSelectedStageId] = useState("");
  const contentRef = useRef<HTMLDivElement>(null);
  const [minHeight, setMinHeight] = useState<number | undefined>(undefined);
  const isInitializedRef = useRef(false);

  const stages = useDealStages().data?.stages;
  const stagesDataMap = useMemo(
    () => (stages ? createDataMapById(stages) : {}),
    [stages]
  );

  const processedChartData = useMemo(() => {
    if (
      !directReportsData?.directReports ||
      isEmpty(directReportsData.directReports)
    ) {
      return chartData;
    }

    const firstReport = directReportsData.directReports[0];

    const processedStages = firstReport.stages
      .map((stage) => {
        const stageId = stage.id.toString();
        const stageData = stagesDataMap[stageId];
        if (!stageData) return null;

        return {
          ...stage,
          stageData,
          stageOrder: stageData.stageOrder
        };
      })
      .filter((stage): stage is NonNullable<typeof stage> => stage !== null)
      .sort((a, b) => a.stageOrder - b.stageOrder)
      .reduce<StageAverageDurationChartDataType[]>((acc, stage) => {
        const prevStart = acc.length
          ? acc[acc.length - 1].start + acc[acc.length - 1].duration
          : 0;

        acc.push({
          id: stage.id.toString(),
          duration: daysToWeeks(stage.averageTimeSpentValues.you || 0),
          start: prevStart,
          label: stage.stageData.name ?? t("unknown"),
          milestone: stage.stageData.milestone,
          team: daysToWeeks(stage.averageTimeSpentValues.team || 0),
          org: daysToWeeks(stage.averageTimeSpentValues.org || 0)
        });

        return acc;
      }, []);

    const milestonesWithStages = new Set(
      processedStages.map((stage) => Number(stage.milestone))
    );

    const processedMilestones = firstReport.milestones
      .filter((milestone) => milestonesWithStages.has(milestone.id))
      .reduce<StageAverageDurationChartDataType[]>((acc, milestone) => {
        const milestoneId = milestone.id as DealMilestone;
        const prevStart = acc.length
          ? acc[acc.length - 1].start + acc[acc.length - 1].duration
          : 0;

        acc.push({
          id: milestoneId.toString(),
          duration: daysToWeeks(milestone.averageTimeSpentValues.you || 0),
          start: prevStart,
          label: t(
            `deals.dealView.milestone.${camelCase(DealMilestone[milestoneId])}`
          ),
          milestone: milestoneId,
          team: daysToWeeks(milestone.averageTimeSpentValues.team || 0),
          org: daysToWeeks(milestone.averageTimeSpentValues.org || 0),
          industry: daysToWeeks(milestone.averageTimeSpentValues.industry || 0)
        });

        return acc;
      }, []);

    return {
      stages: processedStages,
      milestones: processedMilestones
    };
  }, [directReportsData, chartData, stagesDataMap, t]);

  useEffect(() => {
    if (processedChartData && processedChartData[mainSelectedTab]) {
      // Only set the stage/tab on initial mount or when mainSelectedStageId/mainSelectedTab props change
      if (!isInitializedRef.current || mainSelectedStageId) {
        setSelectedStageId(
          isEmpty(mainSelectedStageId)
            ? processedChartData[mainSelectedTab][0]?.id || ""
            : mainSelectedStageId
        );
        setSelectedTab(mainSelectedTab);
        isInitializedRef.current = true;
      }
    }
  }, [mainSelectedStageId, mainSelectedTab, processedChartData]);

  const { breadcrumbIds, setSelectedUserId, dynamicTableData, isLoading } =
    useStageDurationBreadcrumbs();

  useEffect(() => {
    if (processedChartData && processedChartData[selectedTab]) {
      if (!selectedStageId && processedChartData[selectedTab][0]) {
        // If no stage is selected, select the first one
        setSelectedStageId(processedChartData[selectedTab][0].id);
      } else if (selectedStageId) {
        // If a stage is selected, verify it still exists in the new data
        const stageExists = processedChartData[selectedTab].some(
          (element) => element.id === selectedStageId
        );
        if (!stageExists && processedChartData[selectedTab][0]) {
          setSelectedStageId(processedChartData[selectedTab][0].id);
        }
      }
    }
  }, [processedChartData, selectedTab, selectedStageId]);

  useEffect(() => {
    if (contentRef.current && !minHeight && !isLoading) {
      const height = contentRef.current.offsetHeight;
      if (height > 0) {
        setMinHeight(height);
      }
    }
  }, [dynamicTableData, isLoading, minHeight]);

  const stageGroups = groupBy(processedChartData["stages"], "milestone");

  const renderMilestone = (
    milestone: StageAverageDurationChartDataType,
    index: number
  ) => (
    <div
      className={cn(
        "p-3 flex gap-2 items-center",
        "transition-all duration-200 ease-in",
        selectedStageId === milestone.id ? "bg-surface-elevation0" : ""
      )}
      key={index}
    >
      <Avatar className="!size-5 !rounded-lg !text-[10px] !text-on-surface">
        <AvatarFallback
          style={{
            backgroundColor: getColorForMilestoneByType(milestone.milestone)
          }}
        >
          {index + 1}
        </AvatarFallback>
      </Avatar>
      <Tooltip>
        <TooltipTrigger className="flex items-center">
          <Text
            as="div"
            variant={
              selectedStageId === milestone.id ? "label" : "bodySecondary"
            }
          >
            {milestone.label}
          </Text>
        </TooltipTrigger>
        <TooltipContent
          data-test="stage-tooltip"
          className="flex flex-col gap-2 max-w-48"
        >
          <Text as="div" variant="labelSecondary">
            {milestone.label}
          </Text>
          <Text as="div" variant="body">
            {t(
              `stageAverageDuration.descriptions.${camelCase(milestone.label)}`
            )}
          </Text>
        </TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger className="text-tertiary text-sm flex items-center">
          {`${stageGroups[milestone.milestone].length} ${t("stageAverageDuration.stages")}`}
        </TooltipTrigger>
        <TooltipContent data-test="stages-tooltip">
          <Text as="div" variant="body" className="flex flex-col gap-1">
            {map(stageGroups[milestone.milestone], (stage, index) => (
              <div key={index}>{stage.label}</div>
            ))}
          </Text>
        </TooltipContent>
      </Tooltip>
    </div>
  );

  const renderStage = (
    stage: StageAverageDurationChartDataType,
    index: number
  ) => (
    <div
      className={cn(
        "p-3 flex gap-2 items-center",
        "transition-all duration-200 ease-in",
        selectedStageId === stage.id ? "bg-surface-elevation0" : ""
      )}
      key={index}
    >
      <Avatar className="!size-5 !text-[10px] !text-on-surface">
        <AvatarFallback
          style={{
            backgroundColor: getColorForMilestoneByType(
              stage.milestone || undefined
            )
          }}
        >
          {index + 1}
        </AvatarFallback>
      </Avatar>
      <Text
        as="div"
        variant={selectedStageId === stage.id ? "label" : "bodySecondary"}
      >
        {stage.label}
      </Text>
    </div>
  );

  const selectedElement = find(
    processedChartData[selectedTab],
    (element) => element.id === selectedStageId
  );

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      contentClassname="w-[95%]"
      title={
        <div className="flex">
          <div className="flex flex-col gap-3 flex-1">
            <div className="flex flex-col flex-nowrap items-start justify-between align-start">
              <UserBreadcrumbs
                title={t("stageAverageDuration.title")}
                subtitle={t("stageAverageDuration.byDirectReport")}
                ids={breadcrumbIds}
                setter={setSelectedUserId}
              />
            </div>
          </div>
          <div className="flex-0 justify-center">
            <Tabs
              options={{
                [STAGES]: t("stageAverageDuration.stages"),
                [MILESTONES]: t("stageAverageDuration.milestones")
              }}
              defaultValue={selectedTab}
              valueSelected={(value) => {
                if (processedChartData[value][0]) {
                  setSelectedStageId(processedChartData[value][0].id);
                  setSelectedTab(value);
                }
              }}
            />
          </div>
          <div className="flex-1 flex justify-end items-start">
            <div className="flex gap-4 items-center text-h3">
              <Dropdown
                {...periodsDropdownProps(t)}
                defaultValue={periodEnumToString(period)}
                valueSelected={(value) =>
                  periodSelect(periodStringToEnum(value))
                }
              />
              <FeedbackButton
                tooltip={t("feedback")}
                idContext="duration-drilldown"
                variant="iconNoBg"
              />
            </div>
          </div>
        </div>
      }
      content={
        <div
          ref={contentRef}
          className="flex h-full w-full"
          style={{ minHeight: minHeight ? `${minHeight}px` : "20rem" }}
        >
          <div className="flex flex-col min-w-72 w-fit">
            {map(processedChartData[selectedTab], (element, index) => (
              <div
                key={index}
                onClick={() => setSelectedStageId(element.id)}
                data-test={`${element.id}-element`}
                className={cn(
                  "w-full cursor-pointer border-border-border1 border-r",
                  selectedStageId === element.id &&
                    "border-t border-b border-r-0",
                  selectedStageId === element.id && index === 0 && "border-t-0"
                )}
              >
                {selectedTab === STAGES
                  ? renderStage(element, index)
                  : renderMilestone(element, index)}
              </div>
            ))}
            <div className="h-full border-border-border1 border-r" />
          </div>
          <DurationDrilldown
            selectedElement={selectedElement}
            selectedStageId={selectedStageId}
            selectedTab={selectedTab}
          />
        </div>
      }
    />
  );
};

export default DurationDrilldownModal;
